import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../../../components/ui/card';
import { Switch } from '../../../../components/ui/switch';
import { Label } from '../../../../components/ui/label';
import { Input } from '../../../../components/ui/input';
import { Alert, AlertTitle, AlertDescription } from '../../../../components/ui/alert';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useToast } from '../../../../hooks/use-toast';
import api from '../../../../services/apiServices';

function VariantStatCard({ title, stats }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Started</span>
          <span className="font-medium">{stats?.started ?? 0}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Submitted</span>
          <span className="font-medium">{stats?.submitted ?? 0}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Conversion</span>
          <span className="font-medium">
            {stats?.conversion_rate === null || stats?.conversion_rate === undefined ? '—' : `${stats.conversion_rate}%`}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ABTestingTab({ categoryId, locale }) {
  const [config, setConfig] = useState({ ab_test_enabled: false, test_percentage: 0 });
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const { toast } = useToast();

  const fetchConfig = useCallback(async () => {
    if (!categoryId) return;
    setLoadingConfig(true);
    try {
      const res = await api.get(`/admin/categories/cat-qna/ab-config/${categoryId}`, { params: { locale } });
      setConfig({
        ab_test_enabled: res.data.ab_test_enabled,
        test_percentage: res.data.test_percentage,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Failed to Load A/B Config",
        description: err?.response?.data?.detail || err.message || "An unexpected error occurred.",
      });
    } finally {
      setLoadingConfig(false);
    }
  }, [categoryId, locale, toast]);

  const fetchStats = useCallback(async () => {
    if (!categoryId) return;
    setLoadingStats(true);
    try {
      const res = await api.get(`/admin/categories/cat-qna/ab-stats/${categoryId}`, { params: { locale } });
      setStats(res.data);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Failed to Load A/B Stats",
        description: err?.response?.data?.detail || err.message || "An unexpected error occurred.",
      });
    } finally {
      setLoadingStats(false);
    }
  }, [categoryId, locale, toast]);

  useEffect(() => {
    fetchConfig();
    fetchStats();
  }, [fetchConfig, fetchStats]);

  const handleSave = async () => {
    setSavingConfig(true);
    try {
      const percentage = Math.max(0, Math.min(100, Number(config.test_percentage) || 0));
      const res = await api.put(`/admin/categories/cat-qna/ab-config/${categoryId}`, {
        ab_test_enabled: config.ab_test_enabled,
        test_percentage: percentage,
      }, { params: { locale } });
      setConfig({
        ab_test_enabled: res.data.ab_test_enabled,
        test_percentage: res.data.test_percentage,
      });
      toast({
        title: "A/B Test Settings Saved",
        description: "Publish this category's Q&A for the change to reach buyers (subject to the usual publish cache delay).",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Failed to Save A/B Config",
        description: err?.response?.data?.detail || err.message || "An unexpected error occurred.",
      });
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>A/B Test Settings</CardTitle>
          <CardDescription>
            Tag questions as Control/Test in the Edit tab, set a rollout percentage here, then Publish.
            Changes here only reach buyers after the next Publish click, and are subject to the same
            cache delay (up to ~1 hour) as any other question edit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-3">
            <Switch
              id="ab-test-enabled"
              checked={!!config.ab_test_enabled}
              disabled={loadingConfig || savingConfig}
              onCheckedChange={(checked) => setConfig(prev => ({ ...prev, ab_test_enabled: checked }))}
            />
            <Label htmlFor="ab-test-enabled" className="font-normal cursor-pointer">
              Enable A/B testing for this category + locale
            </Label>
          </div>
          <div>
            <Label htmlFor="ab-test-percentage">Test percentage (% of visitors shown the Test set)</Label>
            <Input
              id="ab-test-percentage"
              type="number"
              min={0}
              max={100}
              value={config.test_percentage}
              disabled={!config.ab_test_enabled || loadingConfig || savingConfig}
              onChange={(e) => setConfig(prev => ({ ...prev, test_percentage: e.target.value }))}
              className="max-w-[150px]"
            />
          </div>
          {config.ab_test_enabled && (
            <Alert variant="default">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Remember to tag questions</AlertTitle>
              <AlertDescription>
                If no question is tagged "Test only" in the Edit tab, the Test set will be identical to Control.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
        <CardFooter>
          <Button onClick={handleSave} disabled={loadingConfig || savingConfig}>
            {savingConfig && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Settings
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Funnel Stats</CardTitle>
            <CardDescription>Sessions that started answering vs. sessions that submitted a lead, per variant.</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchStats} disabled={loadingStats}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loadingStats ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loadingStats && !stats ? (
            <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <VariantStatCard title="Control" stats={stats?.control} />
              <VariantStatCard title="Test" stats={stats?.test} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
